import { useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import { useTranslation } from 'react-i18next'
import type { TiptapEditor } from '@/domains/types/editor'
import {
  useEditorImageUpload,
  type EditorImageUploadErrorReason,
} from '@/application/hooks/useEditorImageUpload'
import { RichTextToolbar } from '@/ui/components/common/RichTextToolbar'
import { SizedImage } from '@/ui/components/common/extensions/sized-image.extension'
import { classNames } from '@/ui/utils/main'

interface RichTextEditorProps {
  value: string
  onChange: (markdown: string) => void
  placeholder?: string
  disabled?: boolean
  /** Whether the image toolbar button + paste/drop upload are enabled. Defaults to `true`. */
  allowImages?: boolean
  /** Smaller min-height variant, used in dialog contexts. */
  compact?: boolean
  /**
   * Caps the content area to a fixed max-height with its own internal
   * scrollbar, instead of letting it (and the form around it) grow
   * unbounded. The toolbar sits outside the scrolling region so it always
   * stays visible. Used by edit forms whose description can grow
   * arbitrarily long.
   */
  scrollable?: boolean
  onUploadError?: (message: string) => void
}

/**
 * Markdown-backed rich text editor (Tiptap/ProseMirror) shared by every
 * description/notes field. Serializes to markdown on every change and only
 * re-syncs external `value` changes that didn't originate from this editor,
 * so the caret never jumps mid-keystroke.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  allowImages = true,
  compact,
  scrollable,
  onUploadError,
}: RichTextEditorProps) {
  const { t } = useTranslation()
  const lastEmittedValue = useRef(value)

  const handleUploadError = (reason: EditorImageUploadErrorReason) => {
    const translationKey =
      reason === 'too_large'
        ? 'editor.image_too_large'
        : reason === 'invalid_type'
          ? 'editor.image_invalid_type'
          : 'editor.upload_failed'
    onUploadError?.(t(translationKey))
  }

  const { isUploading, openFilePicker, fileInputProps, handlePaste, handleDrop } =
    useEditorImageUpload({ onUploadError: handleUploadError })

  const editor = useEditor({
    extensions: [
      // StarterKit registers `Underline` by default (Tiptap 3) — do not add
      // `@tiptap/extension-underline` separately, it would duplicate it.
      StarterKit,
      Markdown.configure({ breaks: true }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      ...(allowImages
        ? [
            SizedImage.configure({
              allowBase64: false,
              inline: false,
              resize: {
                enabled: true,
                minWidth: 60,
                minHeight: 40,
                alwaysPreserveAspectRatio: true,
              },
            }),
          ]
        : []),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
      },
      handlePaste: (_view: unknown, event: ClipboardEvent) => {
        if (!allowImages || !editor) return false
        return handlePaste(event, editor)
      },
      handleDrop: (_view: unknown, event: DragEvent) => {
        if (!allowImages || !editor) return false
        return handleDrop(event, editor)
      },
    },
    onUpdate: ({ editor: updatedEditor }: { editor: TiptapEditor }) => {
      const markdown: string =
        updatedEditor.storage.markdown?.getMarkdown?.() ?? updatedEditor.getText()
      lastEmittedValue.current = markdown
      onChange(markdown)
    },
  })

  // Reflect `disabled` on the live editor instance.
  useEffect(() => {
    if (editor) editor.setEditable(!disabled)
  }, [editor, disabled])

  // Only re-sync `value` into the editor when it changed from *outside*
  // (e.g. cancel/reset, or the parent reloading fresh data) — never when it
  // matches the markdown this editor itself just emitted, otherwise every
  // keystroke would reset the cursor position.
  useEffect(() => {
    if (!editor) return
    if (value === lastEmittedValue.current) return
    lastEmittedValue.current = value
    editor.commands.setContent(value)
  }, [editor, value])

  return (
    <div
      className={classNames(
        'rich-text-editor',
        compact && 'rich-text-editor--compact',
        scrollable && 'rich-text-editor--scrollable',
      )}
    >
      <RichTextToolbar
        editor={editor}
        allowImages={allowImages}
        isUploadingImage={isUploading}
        onInsertImage={() => openFilePicker(editor)}
      />
      <EditorContent editor={editor} />
      {allowImages && <input {...fileInputProps} />}
    </div>
  )
}
